/**
 * Calendar-day due-window helpers shared by the API and the web Goals page.
 *
 * WEB-06 root cause: the Goals "Due in 30d" count compared the API's
 * `targetDate` (parsed by `new Date('YYYY-MM-DD')` → UTC midnight) against
 * `Date.now()` (wall-clock) and `Math.ceil`'d the millisecond delta. For a
 * client in a timezone behind UTC, late in the day, that flips a boundary goal
 * in/out of the window depending on the hour — a goal due *today* could round
 * to 0 or 1 inconsistently. These helpers normalize BOTH endpoints to UTC
 * calendar dates (midnight) before differencing, so the result is a stable
 * whole-day count independent of intra-day time and timezone.
 *
 * Lives in the API package (which has a test runner) and is re-exported to the
 * web bundle via `@bw-dwh/api/lib/dates`; the web package has no vitest of its
 * own, so hosting the pure logic + its regression test here keeps it covered.
 */

/**
 * Whole calendar days from `now` until `targetDate` (an ISO `YYYY-MM-DD`
 * string or any Date-parseable value). Positive = future, 0 = today, negative
 * = past. Returns `null` when the input is missing or unparseable.
 */
export function daysUntil(
  targetDate: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!targetDate) return null;
  const target = new Date(targetDate);
  if (Number.isNaN(target.getTime())) return null;
  const targetUtcDay = Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), target.getUTCDate());
  const nowUtcDay = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((targetUtcDay - nowUtcDay) / 864e5);
}

/**
 * True when `targetDate` falls within the next `withinDays` calendar days,
 * inclusive of today (day 0) and the boundary day. Past dates are excluded.
 */
export function isDueWithin(
  targetDate: string | null | undefined,
  withinDays: number,
  now: Date = new Date(),
): boolean {
  const days = daysUntil(targetDate, now);
  return days !== null && days >= 0 && days <= withinDays;
}
